/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {Timeslot} from "types/entities/Timeslot";
import {PackageCategory} from "types/enums/PackageCategory";
import {ThunkAction} from "redux-thunk";
import BookingAction, {BookPackageCreditItems} from "store/booking/BookingAction";
import {
    getAvailableDatesForCategory,
    getCurrentDateIndex,
    getPackageCategoryName,
    getPossibleSlotIndex,
    getSelectedCompetitionPackages,
    getSelectedUpsellPackages,
    getTimeslotsAvailable,
    useHallHolidays,
    usePackageItemKartingOptions,
} from "utils/packageUtils";
import {appendQueryParams, translate} from "utils/utility";
import {useTypedSelector} from "helpers/reducers";
import {RefObject, useEffect, useState} from "react";
import {useDispatch} from "useDispatch";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";
import {
    BOOKING_MAXIMUM_DATE,
    CURRENCY,
    DEFAULT_HALL_ID,
    FERIENPASS_MAXIMUM_DATE,
    HAS_HELP_ENABLED,
} from "helpers/config";
import {BookingSteps} from "types/enums/BookingSteps";
import {RouteComponentProps} from "react-router-dom";
import {useDebouncedDispatch} from "utils/useDebounceQueue";
import {useReactGoogleAnalytics} from "utils/useReactGoogleAnalytics";
import {GAEventName} from "types/entities/specific/GAEventName";
import {useHallOpeningHours} from "utils/hooks/useHallOpeningHours";
import {DataLayerItem} from "types/entities/specific/DataLayerItem";
import {PackagesCart} from "types/entities/PackagesCart";
import {usePackageCategory} from "utils/hooks/usePackageCategory";
import dayjs from "dayjs";

const useCustomTimeslotsNew = (
    setBookingStep: (bookingStep: BookingSteps) => void,
    history: RouteComponentProps["history"],
    // match: RouteComponentProps<BookingTimeslotsNewParams>["match"],
    scrollRefs: [RefObject<HTMLDivElement>, RefObject<HTMLDivElement>, RefObject<HTMLDivElement>] //* timeslots,customTimeslot, confirmTimeslots */
) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const bookingTimeslots = useTypedSelector(state => state.bookingState.timeslots);
    const customSelectedTimeslotState = useTypedSelector(state => state.bookingState.bookingProcess.customTimeslots);
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const packageItemsSelectedTimeslot = useTypedSelector(state => state.bookingState.bookingProcess.selectedTimeslot);
    const selectedPackageCreditItems = useTypedSelector(state => state.bookingState.bookingProcess.selectedPackageCreditItems);

    const hallState = useTypedSelector(state => state.hallState.hall);
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const possibleTimeslots = useTypedSelector(state => state.bookingState.bookingProcess.possibleTimeslots);
    const selectedPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const packages = useTypedSelector(state => state.bookingState.packages);
    const selectedPackageCategory = useTypedSelector(state => state.bookingState.bookingProcess.filter.category);
    const selectedTimeslotFromCart = packagesCart?.packagesCartItemSlots?.find((item, index) => index === 0);
    const selectedUpsells = useTypedSelector(state => state.bookingState.bookingProcess.upsell);
    const selectedCompetitions = useTypedSelector(state => state.bookingState.bookingProcess.competition);

    const timeslots = bookingTimeslots?.map(bookingTimeslot => bookingTimeslot.timeslots) || [];
    // @ts-ignore
    const flattenedTimeslots: Timeslot[] = [].concat(...timeslots);
    const cartSelectedTimeslot = flattenedTimeslots.find(timeslot => timeslot.id === selectedTimeslotFromCart?.timeslot_id) || null;

    const [availableDates, setAvailableDates] = useState<Date[]>([]);
    const [customSelectedTimeslots, setCustomSelectedTimeslots] = useState<Timeslot[]>([]);
    const [firstPossibleSlotIndex, setFirstPossibleSlotIndex] = useState<number | null>(null);
    const [isLoadingTimeslots, setIsLoadingTimeslots] = useState(true);
    const [isFetchingTimeslot, setIsFetchingTimeslot] = useState(false);
    const [lastPossibleSlotIndex, setLastPossibleSlotIndex] = useState<number | null>(null);
    const [selectCustomSlotsEnabled, setSelectCustomSlotsEnabled] = useState<boolean>(false);
    const [selectedTimeslot, setSelectedTimeslot] = useState<Timeslot | null>(cartSelectedTimeslot || null);
    const [stepsEnabled, setStepsEnabled] = useState<boolean>(false);
    const voucherItems = useTypedSelector(state => state.voucherState.voucherItems);
    const {debouncedDispatch} = useDebouncedDispatch();
    const {sendGenericEvent} = useReactGoogleAnalytics();
    const currentDateIndex = getCurrentDateIndex(availableDates, filter);
    const selectedUpsellPackages = getSelectedUpsellPackages(selectedUpsells);
    const selectedCompetitionPackages = getSelectedCompetitionPackages(selectedCompetitions);
    const timeslotsAvailable = getTimeslotsAvailable(flattenedTimeslots, hallState, selectedPackage);
    const {getExcludedDays} = useHallOpeningHours();
    const {getBookingPackageItems, getBookingPackageItemsForEvent} = usePackageItemKartingOptions();
    const {getCategoryBySlug} = usePackageCategory();
    const {includedDates, filteredExcludedDays} = useHallHolidays(selectedPackageCategory);

    const resetCustomSelectedTimeslotHandler = () => {
        setFirstPossibleSlotIndex(null);
        setLastPossibleSlotIndex(null);
        return dispatch(BookingAction.resetCustomTimeslot());
    };

    const setStepsEnabledHandler = (value: boolean) => {
        if (value === true) {
            resetCustomSelectedTimeslotHandler();
            setSelectedTimeslot(null);
            setPossibleTimeslotsHandler([]);
            setFirstPossibleSlotIndex(null);
            setLastPossibleSlotIndex(null);
            setCustomSelectedTimeslots([]);
            if (scrollRefs != null && scrollRefs.length > 0 && scrollRefs[0].current != null) {
                scrollRefs[0].current.scrollIntoView({behavior: "smooth"});
            }
        }
        setStepsEnabled(value);
    };
    const setPossibleTimeslotsHandler = (timeslots: Timeslot[]) => {
        return dispatch(BookingAction.setPossibleTimeslots(timeslots));
    };
    const setSelectCustomSlotsEnabledHandler = (enable: boolean) => {
        //provide general reset functionality if this is ever switched back
        resetCustomSelectedTimeslotHandler();
        setSelectCustomSlotsEnabled(enable);

        setSelectedTimeslot(null);
        setPossibleTimeslotsHandler([]);
        setFirstPossibleSlotIndex(null);
        setLastPossibleSlotIndex(null);
        setSelectedTimeslotHandler(selectedTimeslot, enable);
    };

    const possibleSlotIndex = (timeslot: Timeslot, type: "first" | "last") => {
        return getPossibleSlotIndex(timeslot, type, timeslotsAvailable, selectedPackage);
    };

    const setSelectedTimeslotHandler = (timeslot: Timeslot | null, selectCustomSlotsEnabled: boolean) => {
        toast.dismiss();
        // if (!selectedPackage) {
        //     return;
        // }

        if (!selectCustomSlotsEnabled) {
            // if (USE_POSSIBLE_TIMESLOTS_ALLOWED) {
            //     fetchPossibleTimeslots(timeslot);
            // }
            setSelectedTimeslot(timeslot);
            return;
        }

        if (!timeslot) {
            return;
        }
        const customSelectedTimeslotIndex = customSelectedTimeslotState.findIndex(customTimeslot => customTimeslot.id === timeslot.id);

        // if (customSelectedTimeslotIndex !== -1 || (selectCustomSlotsEnabled && selectedPackage.typical_timeslot_count > customSelectedTimeslotState.length)) {
        if (customSelectedTimeslotIndex !== -1 || selectCustomSlotsEnabled) {
            return setCustomSelectedTimeslotHandler(timeslot);
        } else {
            toast.error(t("Too many slots"), {toastId: "tooManySlots"});
        }
    };
    //
    // const fetchPackagesOnDateChange = () => {
    //     const voucherCodes = getVoucherCodes(voucherItems);
    //     dispatch(VoucherAction.resetAppliedVouchers());
    //     debouncedDispatch(BookingAction.fetchPackagesByLocationParams(location, filter, true, voucherCodes));
    // };
    //
    const switchDate = date => {
        setSelectCustomSlotsEnabledHandler(false);
        dispatch(
            BookingAction.setFilter({
                ...filter,
                date: date,
            })
        );
        // fetchPackagesOnDateChange();
    };

    const setCustomSelectedTimeslotHandler = (timeslot: Timeslot) => {
        return dispatch(BookingAction.setCustomTimeslot(timeslot));
    };

    // const bookWithCustomTimeslot = () => {
    //     if (!packagesCart) {
    //         return;
    //     }
    //
    //     if (
    //         !selectedPackage ||
    //         !customSelectedTimeslotState ||
    //         customSelectedTimeslotState.length === 0 ||
    //         customSelectedTimeslotState.length !== selectedPackage.typical_timeslot_count
    //     ) {
    //         return;
    //     }
    //
    //     if (isFetchingTimeslot) {
    //         return;
    //     }
    //     const timeslotIds = customSelectedTimeslotState.map(timeslot => timeslot.id);
    //     setIsFetchingTimeslot(true);
    //     dispatch<ThunkAction<any, any, any, any>>(
    //         BookingAction.fetchBookPackageWithCustomTimeslots(
    //             {
    //                 package_id: +match.params.package_category_name,
    //                 timeslot_ids: timeslotIds,
    //                 adults: filter.adults,
    //                 children: filter.children,
    //                 upsell: selectedUpsellPackages,
    //                 hours_count: filter.hours_count ? filter.hours_count : 0,
    //             },
    //             packagesCart
    //         )
    //     ).then(r => {
    //         history.push("/booking/customer" + "?" + appendQueryParams(filter));
    //         setIsFetchingTimeslot(false);
    //     });
    //
    //     setBookingStep(BookingSteps.Customer);
    // };

    const sendGAData = (packagesCartRes: PackagesCart) => {
        if (!packagesCartRes?.packagesCartItems) {
            return;
        }

        const items: DataLayerItem[] = packagesCartRes.packagesCartItems.map(packageItem => {
            const category = packages.find(packageS => packageS.id === packageItem.package_id);
            const item: DataLayerItem = {
                item_id: packageItem.package_id,
                item_name: packageItem?.packageItem
                    ? translate(packageItem.packageItem.name)
                    : getPackageCategoryName(category?.category ? category.category : ""),
                quantity: packageItem.drivers_count_adult + packageItem.drivers_count_child,
                item_variant: "",
                price: packageItem.price / (packageItem.drivers_count_adult + packageItem.drivers_count_child),
                currency: CURRENCY,
            };
            return item;
        });

        sendGenericEvent(GAEventName.AddToCart, packagesCartRes.price, items);
    };

    const bookWithAutomaticTimeslot = (timeslot?: Timeslot | null, forEvent: boolean = false, withoutClearAvailablePackagesItems: boolean = false) => {
        setIsLoading(true);
        // if (!packagesCart) {
        //     return;
        // }
        const package_item_ids = forEvent ? getBookingPackageItemsForEvent() : getBookingPackageItems();
        if (timeslot) {
            dispatch(BookingAction.setPackageItemsSelectedTimeslot(timeslot));
            history.push("/booking/packages" + "?" + appendQueryParams(filter));
            selectBookingStepHandler();
            setIsFetchingTimeslot(false);

            if (!packagesCart) {
                dispatch(BookingAction.initCart(true)).finally(() => {
                    setIsLoading(false);
                });
            }
            dispatch(BookingAction.clearAvailablePackagesItems());

            return;
        }
        if (isFetchingTimeslot || !filter.category || !packagesCart || !packageItemsSelectedTimeslot) {
            console.log("something isnt", isFetchingTimeslot, filter.category, packagesCart, packageItemsSelectedTimeslot);
            dispatch(BookingAction.clearAvailablePackagesItems());
            return;
        }

        const newPackageCreditItems: BookPackageCreditItems[] = selectedPackageCreditItems
            .filter(item => item.id !== null)
            .map(item => {
                return {
                    package_item_id: item.id as number,
                    quantity: item.quantity,
                    personIndex: item.personIndex,
                };
            });

        dispatch<ThunkAction<any, any, any, any>>(
            BookingAction.fetchBookPackageNew(
                {
                    package_category_name: filter.category,
                    package_item_ids: package_item_ids,
                    activity_ids: filter.activity_ids.length === 0 ? [1] : filter.activity_ids,
                    timeslot_id: packageItemsSelectedTimeslot.id,
                    adults: filter.adults,
                    children: filter.children,
                    upsell: [...selectedUpsellPackages, ...selectedCompetitionPackages],
                    credits: newPackageCreditItems ?? [],
                },
                packagesCart,
                "useCustomTimeslotsNew - bookWithAutomaticTimeslot"
            )
        )
            .then(r => {
                history.push("/booking/customer" + "?" + appendQueryParams(filter));
                setIsFetchingTimeslot(false);
                // sendGAData(r.packageCart);
                sendGAData(r.packageCart);
            })
            .finally(() => {
                setIsLoading(false);
                if (!withoutClearAvailablePackagesItems) {
                    dispatch(BookingAction.clearAvailablePackagesItems());
                }
            });
        setIsFetchingTimeslot(true);
    };

    const bookAdditionalUpsells = (packagesCart: PackagesCart) => {
        // if (!packagesCart) {
        //     return;
        // }

        dispatch<ThunkAction<any, any, any, any>>(
            BookingAction.fetchBookUpsells(
                {
                    // upsell: [...selectedUpsellPackages, ...selectedCompetitionPackages],
                    upsell: selectedUpsellPackages,
                    adults: filter.adults,
                    children: filter.children,
                },
                packagesCart,
                "useCustomTimeslotsNew - bookWithAutomaticTimeslot"
            )
        )
            .then(r => {
                history.push(
                    "/booking/login/reservations/detail/" +
                        packagesCart.reservation_id +
                        "/upsells/" +
                        packagesCart.guid +
                        "/summary" +
                        "?" +
                        appendQueryParams(filter)
                );
                setIsFetchingTimeslot(false);
            })
            .finally(() => {
                setIsLoading(false);
            });
        setIsFetchingTimeslot(true);
    };

    // const fetchPossibleTimeslots = (timeslot: Timeslot | null) => {
    //     if (!timeslot) {
    //         //if I want to clear out the timeslots, reset everything
    //         return setPossibleTimeslotsHandler([]);
    //     }
    //     if (!packagesCart) {
    //         return;
    //     }
    //     if (selectedPackage && selectedPackage.category === PackageCategory.Event) {
    //         return;
    //     }
    //
    //     dispatch<ThunkAction<any, any, any, any>>(
    //         BookingAction.fetchPossibleTimeslots(
    //             {
    //                 package_id: +match.params.package_category_name,
    //                 timeslot_id: timeslot.id,
    //                 adults: filter.adults,
    //                 children: filter.children,
    //                 upsell: selectedUpsellPackages,
    //                 hours_count: filter.hours_count ? filter.hours_count : 0,
    //             },
    //             packagesCart
    //         )
    //     ).then(r => {
    //         if ("timeslots" in r) {
    //             setPossibleTimeslotsHandler(r.timeslots);
    //         } else {
    //             setPossibleTimeslotsHandler([]);
    //         }
    //     });
    // };

    const selectBookingStepHandler = () => {
        //setSelectedTimeslot(timeslot);
        setBookingStep(BookingSteps.Customer);
    };

    const fetchPackageTimeslots = () => {
        setIsLoadingTimeslots(true);
        dispatch<ThunkAction<any, any, any, any>>(
            // BookingAction.fetchPackagesTimeSlots({
            BookingAction.fetchCategoryTimeSlots({
                adults: filter.adults,
                children: filter.children,
                date: filter.date,
                package_category_name: filter?.category ? filter.category : PackageCategory.General,
                hall_id: DEFAULT_HALL_ID,
                activity_ids: filter.activity_ids.length === 0 ? [1] : [...filter.activity_ids],
                timeslot_id: selectedTimeslot ? selectedTimeslot.id : null,
            })
        ).then(r => {
            setIsLoadingTimeslots(false);
            if (r.timeslotsAllowed === false) {
                //@todo refactor this into Package Submit
                // dispatch(BookingAction.fetchBookPackage({
                //     package_id: +props.match.params.package_category_name,
                //     adults: filter.adults,
                //     children: filter.children,
                //     upsell: [],
                // })).then(r => {
                //     consoleLog(r);
                //     props.history.push("/booking/customer");
                // });
            }
        });
    };

    useEffect(() => {
        if (selectedPackageCategory) {
            const packageEntity = getCategoryBySlug(selectedPackageCategory);
            const filteredDays = filteredExcludedDays(getExcludedDays());
            let availableDatesForCategory = getAvailableDatesForCategory(selectedPackageCategory, packageEntity, filteredDays, includedDates);
            const bookingMaximumDate = BOOKING_MAXIMUM_DATE;
            availableDatesForCategory = availableDatesForCategory.filter(date => {
                return date.getTime() <= bookingMaximumDate.getTime();
            });

            if (selectedPackageCategory === PackageCategory.School) {
                const specificDatesToRemove = [
                    dayjs("2025-06-16").toDate(),
                    dayjs("2025-06-17").toDate(),
                    dayjs("2025-06-23").toDate(),
                    dayjs("2025-06-24").toDate(),
                    dayjs("2025-06-30").toDate(),
                    dayjs("2025-07-01").toDate(),
                ];

                availableDatesForCategory = [...availableDatesForCategory, ...specificDatesToRemove];
                // needs to be sorted, as we are always selecting buttons based by next index
                availableDatesForCategory.sort((a, b) => a.getTime() - b.getTime());
            }

            if (
                selectedPackageCategory == PackageCategory.FerienPass ||
                selectedPackageCategory == PackageCategory.FerienPassB ||
                selectedPackageCategory == PackageCategory.ThreeTwo
            ) {
                const maximumDate = packageEntity.extra?.maximumDate ? new Date(packageEntity.extra.maximumDate) : FERIENPASS_MAXIMUM_DATE;
                availableDatesForCategory = availableDatesForCategory.filter(date => {
                    const limit = dayjs("2025-08-01").toDate();
                    if (date.getTime() == limit.getTime()) {
                        return false;
                    }
                    const dayOfWeek = date.getDay();
                    return dayOfWeek !== 0 && dayOfWeek !== 6 && date.getTime() < maximumDate.getTime();
                });
            }
            if (selectedPackageCategory === PackageCategory.MegaFunSchool) {
                availableDatesForCategory = availableDatesForCategory.filter(date => {
                    const limit = dayjs("2025-08-01").toDate();
                    if (date.getTime() == limit.getTime()) {
                        return false;
                    }
                    const dayOfWeek = date.getDay();
                    return dayOfWeek !== 0 && dayOfWeek !== 6;
                });
            }
            setAvailableDates(availableDatesForCategory);
        }
    }, [filter.date]);

    useEffect(() => {
        setCustomSelectedTimeslots(customSelectedTimeslotState);
        if (customSelectedTimeslotState.length > 0) {
            setLastPossibleSlotIndex(possibleSlotIndex(customSelectedTimeslotState[0], "last"));
            setFirstPossibleSlotIndex(possibleSlotIndex(customSelectedTimeslotState[0], "first"));
        }
    }, [customSelectedTimeslots, customSelectedTimeslotState]);

    useEffect(() => {
        // if (!packagesCart) {
        //     history.replace("/booking/package/" + match.params.package_category_name);
        //     return;
        // }
        setIsLoadingTimeslots(true);
        const timedFetch = setTimeout(() => {
            fetchPackageTimeslots();
        }, 250);
        return () => {
            clearTimeout(timedFetch);
        };
    }, [filter.category, filter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (selectedPackage && selectedPackage.category !== PackageCategory.Event) {
                setStepsEnabled(HAS_HELP_ENABLED);
            }
        }, 500);
        return () => {
            setStepsEnabled(false);
            clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        if (selectedTimeslot) {
            setLastPossibleSlotIndex(possibleSlotIndex(selectedTimeslot, "last"));
            setFirstPossibleSlotIndex(possibleSlotIndex(selectedTimeslot, "first"));
        }
    }, [timeslotsAvailable]);

    // useEffect(() => {
    //     if (cartSelectedTimeslot && USE_POSSIBLE_TIMESLOTS_ALLOWED) {
    //         fetchPossibleTimeslots(cartSelectedTimeslot);
    //     }
    // }, [cartSelectedTimeslot]);

    return {
        availableDates,
        bookWithAutomaticTimeslot,
        bookAdditionalUpsells,
        // bookWithCustomTimeslot,
        currentDateIndex,
        customSelectedTimeslots,
        firstPossibleSlotIndex,
        isLoadingTimeslots,
        isFetchingTimeslot,
        lastPossibleSlotIndex,
        possibleTimeslots,
        resetCustomSelectedTimeslotHandler,
        selectCustomSlotsEnabled,
        selectedTimeslot,
        setSelectCustomSlotsEnabledHandler,
        setSelectedTimeslotHandler,
        setStepsEnabledHandler,
        stepsEnabled,
        switchDate,
        timeslotsAvailable,
        isLoading,
    };
};

export default useCustomTimeslotsNew;
